import ReactModal from 'react-modal';

import cs from 'classnames';

import { DirtyTracker, useDirtyTracker } from 'lib/context/DirtyTrackerContext';
import { cn } from 'lib/helpers/utility';

import ModalSidebar from './ModalSidebar/ModalSidebar';
import Footer from './components/Footer';
import Header from './components/Header';
import { MODAL_BODY_SPACE, MODAL_WIDTH } from './lib/index';

import './lib/Modal.scss';

const BaseModal = ({
  isOpen = false,
  handleOpen = false,
  handleClose,
  title,
  customTitle,
  children,
  width = 'small',
  isCentered = false,
  subModal,
  slideFromRight = true,
  isLarge = false,
  disableHeader = false,
  headButton,
  headerClassName,
  headerCloseIcon = true,
  customHeader,
  prevButton,
  footer,
  isFooter = true,
  footerActions,
  className,
  portalClassName,
  shouldCloseOnOverlayClick = true,
  overlayClassName,
  bodyClassName,
  footerClassName,
  customStyling,
  modalCenterAnimation = false,
  isSidebar = false,
  sidebarItems = [],
  onScroll,
  listClassName,
  contentClassName,
  isHidden = false,
  isDirty,
  ...rest
}) => {
  const dirtyTracker = useDirtyTracker();

  // If isDirty is provided and we have DirtyTracker context, protect the close action
  const protectedClose = () => {
    if (isDirty !== undefined && dirtyTracker?.handleProtectedAction) {
      dirtyTracker.handleProtectedAction(handleClose);
    } else {
      handleClose();
    }
  };
  const hiddenStyle = isHidden ? { opacity: 0, pointerEvents: 'none' } : {};

  return (
    <ReactModal
      isOpen={isOpen || handleOpen}
      onRequestClose={protectedClose}
      contentLabel={title}
      closeTimeoutMS={300}
      className={cn(
        'Modal',
        MODAL_WIDTH(width, isCentered),
        !slideFromRight && 'm-6 max-h-[calc(100vh-48px)] sm:m-4 sm:max-h-[calc(100vh-32px)]',
        isLarge && MODAL_WIDTH('full'),
        className
      )}
      overlayClassName={cs(
        'ModalOverlay',
        !isCentered && slideFromRight && 'ModalRightOverlay',
        modalCenterAnimation && 'ModalCenterAnimation',
        overlayClassName
      )}
      portalClassName={cs(portalClassName, subModal && 'SubModal')}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      style={{ content: { ...customStyling, ...hiddenStyle }, overlay: hiddenStyle }}
      {...rest}>
      {!disableHeader && (
        <Header
          title={title}
          customTitle={customTitle}
          headButton={headButton}
          headerClassName={headerClassName}
          headerCloseIcon={headerCloseIcon}
          customHeader={customHeader}
          prevButton={prevButton}
          handleClose={protectedClose}
        />
      )}
      <div
        onScroll={onScroll}
        className={cn(
          'Modal__body',
          MODAL_BODY_SPACE(width, isCentered, isSidebar),
          bodyClassName
        )}>
        {isSidebar ? (
          <ModalSidebar
            items={sidebarItems}
            className={bodyClassName}
            listClassName={listClassName}
            contentClassName={contentClassName}
          />
        ) : (
          children
        )}
      </div>
      {footer ? (
        <div className={cs('Modal__footer h-[56px]', footerClassName)} data-dd-privacy="allow">
          {footer}
        </div>
      ) : (
        <Footer
          footerActions={footerActions}
          handleClose={protectedClose}
          className={footerClassName}
          isFooter={isFooter}
        />
      )}
    </ReactModal>
  );
};

/**
 * @param {import('./lib/propTypes').ModalProps} props
 */

const Modal = ({ isDirty, confirmMessage, confirmKey, ...props }) => {
  if (isDirty !== undefined) {
    return (
      <DirtyTracker isDirty={isDirty} message={confirmMessage} confirmKey={confirmKey}>
        <BaseModal isDirty={isDirty} {...props} />
      </DirtyTracker>
    );
  }
  return <BaseModal {...props} />;
};

export default Modal;
